import { Button } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { createRef, RefObject, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDataLoader } from '../../../hooks/useDataLoader';
import useMobileView from '../../../hooks/useMobileView';
import { usePermission } from '../../../hooks/usePermission';
import { UiComponent } from '../../../interfaces/pagebuilder';
import { setCurrentEditComponent, setPageBuilder, setShowGeneralSettingsOpened, setShowVisibilitySettings } from '../../../reduxToolkit/slices/commonSlice';
import { RootState } from '../../../reduxToolkit/store';
import { useSelector } from '../../../reduxToolkit/typedRedux';
import { toggleComponent } from '../../../services/services';
import VyPrivilegeWrapper from '../../common/VyPrivilegeWrapper';
import PageBuilder from './PageBuilder';
import TemplateControlWrapper from './TemplateControlWrapper';
import { UiComponentBackground } from './UiComponentBackground';

interface PageBuilderContentInterface {
  isLandingPage?: boolean;
}

const PageBuilderContent = ({ isLandingPage = false }: PageBuilderContentInterface) => {
  const event = useSelector((state) => state.common.event);
  const pageData = useSelector((state) => state.common.pagedata);

  const { t } = useTranslation('pagebuilder');
  const pagebuilder = useSelector((state) => state.common.pagebuilder);
  const lockedPBComponents = useSelector((state) => state.common.lockedPBComponents);
  const currentEditComponent = useSelector((state) => state.common.currentEditComponent);

  const dispatch = useDispatch();
  const { loadData } = useDataLoader();
  const { isMobileView } = useMobileView();
  const templateEditMode = useSelector((state: RootState) => state.common.templateEditMode);
  const [onAddComponent, setOnAddComponent] = useState<number>(-1);
  const [onDeleteComponent, setOnDeleteComponent] = useState<number>(-1);
  const [onMoveComponent, setOnMoveComponent] = useState<any>(null);
  const [onMoveOutComponent, setOnMoveOutComponent] = useState<{ componentid: string; pageid: string } | null>(null);
  const [onCopyComponent, setOnCopyComponent] = useState<any>(null);
  const [showTemplateControlIndex, setShowTenplateControlIndex] = useState(-1);

  const componentsRefs = useRef<RefObject<HTMLDivElement>[]>([]);
  const { isEventHost } = usePermission();

  useEffect(() => {
    componentsRefs.current = Array(pagebuilder.currentcomponents.length)
      .fill(0)
      .map(() => createRef());
  }, [pagebuilder.currentcomponents.length]);

  async function handleToggle(componentId: string) {
    const resp = await toggleComponent(componentId);

    const mapItems = (items: UiComponent[], isVisible: boolean) => {
      return items.map((item) => (item._id === componentId ? { ...item, isVisible } : item));
    };

    dispatch(
      setPageBuilder({
        ...pagebuilder,
        originalcomponents: mapItems(pagebuilder.originalcomponents, resp.isVisible),
        currentcomponents: mapItems(pagebuilder.currentcomponents, resp.isVisible),
      })
    );
  }

  return (
    <>
      <PageBuilder
        pageData={pageData}
        isLandingPage={isLandingPage}
        onAddComponent={onAddComponent}
        onEditComponent={currentEditComponent}
        onDeleteComponent={onDeleteComponent}
        onMoveComponent={onMoveComponent}
        onCloseModals={() => {
          dispatch(setCurrentEditComponent(-1));
          dispatch(setShowVisibilitySettings(false));
          dispatch(setShowGeneralSettingsOpened(false));
          setOnAddComponent(-1);
          setOnDeleteComponent(-1);
          setOnMoveComponent(null);
        }}
        onMoveFinished={(index: number) => {
          componentsRefs.current[index].current?.scrollIntoView({ behavior: 'smooth' });
          setOnMoveComponent(null);
        }}
        onMoveOutComponent={onMoveOutComponent}
        onMoveOutFinished={() => {
          setOnMoveOutComponent(null);
        }}
        onCopyComponent={onCopyComponent}
        onCopyFinished={() => {
          setOnCopyComponent(null);
        }}>
        <div className="relative flex flex-col w-full">
          <div className="p-2 pt-4" />
          <div
            onClick={() => setOnAddComponent(0)}
            className={`relative h-${templateEditMode === true ? '4' : '0'} opacity-${
              templateEditMode === true ? '1' : '0'
            } z-10 flex overflow-hidden w-full justify-center transition-all`}>
            <hr className="absolute top-0 bottom-0 left-0 right-0 w-full m-auto border-white border-dashed border-t-1" />
            <VyPrivilegeWrapper privilege="pagebuilder" hideIfDenied>
              {(access) => (
                <Button disabled={!access} className="outline-none" variant="contained" color="secondary">
                  <p style={{ fontSize: '11px', fontWeight: 'bold' }}>{t('pagebuilder:add-new-component')}</p>
                </Button>
              )}
            </VyPrivilegeWrapper>
          </div>

          {pagebuilder.currentcomponents
            .filter((component) => templateEditMode === true || component.isVisible)
            .filter((component) => component.componentType !== 'speeddating' || event?.modules.matching.active)
            .map((component: UiComponent, index: number) => (
              <div
                key={component._id}
                id={component._id}
                className="relative flex flex-col items-center justify-center"
                style={{
                  width: '100%',
                  height: 'auto',
                  scrollMarginTop: '150px',
                  ...(!component.isVisible ? { filter: 'grayscale(100)' } : {}),
                }}
                ref={componentsRefs.current[index]}>
                <UiComponentBackground component={component} />
                {lockedPBComponents?.includes(component._id) && isEventHost() && (
                  <div className="absolute top-0 left-0 z-10 grid w-full h-full bg-black bg-opacity-30 place-items-center" />
                )}
                <div
                  className="flex items-center justify-center"
                  style={{ width: component.isFullWidth ? '100%' : isMobileView() ? '95%' : '80%', height: 'auto', zIndex: 3 }}>
                  <TemplateControlWrapper
                    firstComponent={index === 0}
                    component={component}
                    lastComponent={index === pagebuilder.currentcomponents.length - 1}
                    onAdd={() => setOnAddComponent(index + 1)}
                    onEdit={() => dispatch(setCurrentEditComponent(index))}
                    onDelete={() => setOnDeleteComponent(index)}
                    isVisible={component.isVisible}
                    isFullWidth={component.isFullWidth ?? false}
                    onToggle={() => handleToggle(component._id)}
                    onMoveUp={() =>
                      setOnMoveComponent({
                        componentid: component._id,
                        index: component.componentIndex - 1,
                      })
                    }
                    onMoveDown={() =>
                      setOnMoveComponent({
                        componentid: component._id,
                        index: component.componentIndex + 1,
                      })
                    }
                    onMoveOut={(pageId) => {
                      setOnMoveOutComponent({ componentid: component._id, pageid: pageId });
                    }}
                    onCopy={(pageid) => {
                      setOnCopyComponent({ componentid: component._id, pageid: pageid });

                      if (component.componentType === 'column') {
                        loadData(event!, pageData?.name);
                      }
                    }}
                    onVisibility={() => {
                      dispatch(setCurrentEditComponent(index));
                      dispatch(setShowVisibilitySettings(true));
                    }}
                  />
                </div>
              </div>
            ))}
        </div>
      </PageBuilder>
    </>
  );
};

export default PageBuilderContent;
